import { endOfDay } from 'date-fns';

// Types mirror the grouped response of
// GET /analytics/dashboard/:campaignId/creatorList (PROD-181).
export interface CreatorSubmission {
  submissionId: string;
  submittedOn: string; // ISO timestamp
  platform: string; // lowercase
  invoice: string | null;
  cashback: number;
}

export interface CampaignCreator {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  profileImage?: string | null;
  profileUrl?: string | null;
  userName?: string | null;
  latestPlatform: string | null;
  platforms: string[];
  totalCashback: number;
  submissionCount: number;
  submissions: CreatorSubmission[]; // newest first
}

export interface SubmissionRow {
  userId: string;
  name: string;
  avatarUrl: string | null;
  platforms: string[]; // all-time, most-recently-used first
  submittedOn: string; // representative (latest in range)
  invoice: string | null; // representative's invoice
  totalCashback: number; // all-time sum
}

const inRange = (
  iso: string,
  start: Date | null,
  end: Date | null,
): boolean => {
  const t = new Date(iso).getTime();
  if (start && t < start.getTime()) return false;
  if (end && t > end.getTime()) return false;
  return true;
};

/**
 * Date-range view over the grouped creator list. The range controls row
 * visibility and the representative cashback (latest within range — drives
 * Submitted date + Action invoice); the Paid sum and platforms always
 * aggregate all-time. null bounds (All Time) keep every creator visible.
 */
export const deriveSubmissionRows = (
  creators: CampaignCreator[],
  startDate: Date | null,
  endDate: Date | null,
): SubmissionRow[] => {
  // CalendarFilter hands the end date in at local midnight (00:00). Treat it as
  // the whole day so a submission later on the end day stays in range. No-op for
  // the preset windows whose ends are already end-of-day or "now".
  const endInclusive = endDate ? endOfDay(endDate) : null;
  return creators
    .map((c) => {
      const submissions = c.submissions ?? [];
      // submissions are newest-first, so the first in-range one is the latest
      const representative = submissions.find((s) =>
        inRange(s.submittedOn, startDate, endInclusive),
      );
      if (!representative) return null;
      // $addToSet order is unspecified — derive most-recent-first ordering
      // from the submissions array instead of trusting `platforms`.
      const orderedPlatforms = [...new Set(submissions.map((s) => s.platform))];
      return {
        userId: c.userId,
        name: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim(),
        avatarUrl: c.profileUrl ?? c.profileImage ?? null,
        platforms: orderedPlatforms,
        submittedOn: representative.submittedOn,
        invoice: representative.invoice,
        totalCashback: c.totalCashback,
      };
    })
    .filter((r): r is SubmissionRow => r !== null)
    .sort(
      (a, b) =>
        new Date(b.submittedOn).getTime() - new Date(a.submittedOn).getTime(),
    );
};
